import moment from "moment";

/**
 * Maps application locales to valid BCP 47 locale codes
 * @param locale - The application locale (e.g., 'gb', 'de', 'en')
 * @returns Valid BCP 47 locale code (e.g., 'en-GB', 'de-DE', 'en-US')
 */
export const getLocaleCode = (locale: string): string => {
	const localeMap: { [key: string]: string } = {
		'gb': 'en-GB',  // British English - DD/MM/YYYY format
		'en': 'en-US',  // American English - MM/DD/YYYY format
		'de': 'de-DE',  // German
		'ch': 'de-CH',  // Swiss German
		'fr': 'fr-FR',  // French
		'es': 'es-ES',  // Spanish
		'it': 'it-IT',  // Italian
		'nl': 'nl-NL',  // Dutch
		'pl': 'pl-PL',  // Polish
		'dk': 'da-DK',  // Danish
	};

	return localeMap[locale.toLowerCase()] || locale.toLowerCase() + '-' + locale.toUpperCase();
};

/**
 * Maps application locales to moment.js locale codes (lowercase)
 * @param locale - The application locale (e.g., 'gb', 'de', 'en')
 * @returns moment.js locale code (e.g., 'en-gb', 'de', 'en')
 */
export const getMomentLocale = (locale: string): string => {
	const momentLocaleMap: { [key: string]: string } = {
		'gb': 'en-gb',
		'en': 'en',
		'de': 'de',
		'ch': 'de-ch',
		'fr': 'fr',
		'es': 'es',
		'it': 'it',
		'nl': 'nl',
		'pl': 'pl',
		'dk': 'da',
	};

	return momentLocaleMap[locale.toLowerCase()] || locale.toLowerCase();
};

/**
 * Formats a date string to a locale-aware display string.
 * @param dateToFormat - ISO date string to format
 * @param locale - Application locale (e.g., 'de', 'en', 'gb')
 * @param options - DateTimeFormatOptions (defaults to date + time without seconds)
 * @param includeMilliseconds - Whether to append milliseconds (e.g. for log timestamps)
 */
export const formatDate = (
	dateToFormat: string,
	locale: string,
	options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" },
	includeMilliseconds = false
): string => {
	if (dateToFormat && dateToFormat.length) {
		const dateNumber = Date.parse(dateToFormat);
		if (!isNaN(dateNumber)) {
			const date = new Date(dateToFormat);
			const formatted = date.toLocaleDateString(getLocaleCode(locale), options);
			return includeMilliseconds ? formatted + ":" + date.getMilliseconds() : formatted;
		}
	}
	return dateToFormat;
};

/**
 * Formats a date range as a locale-aware placeholder string
 * Uses moment's 'L' token which provides locale-specific short date format
 * (e.g., DD.MM.YYYY for 'de', DD/MM/YYYY for 'en-gb', MM/DD/YYYY for 'en')
 * @param startDate - The start date as a moment object
 * @param endDate - The end date as a moment object
 * @param locale - The application locale (e.g., 'gb', 'de', 'en')
 * @returns Formatted date range string (e.g., '01.01.2025 - 06.02.2025')
 */
export const formatDateRangePlaceholder = (startDate: moment.Moment, endDate: moment.Moment, locale: string): string => {
	const momentLocale = getMomentLocale(locale);
	return startDate.locale(momentLocale).format('L') + ' - ' + endDate.locale(momentLocale).format('L');
};
